import { useCallback } from 'react';
import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ApplicationSummary } from '../../../shared/VolunteerApplicationServiceTypes';
import { ArrowRightOutlined } from '@ant-design/icons';
import {
	ApplicationPreworkStatus,
	PreworkCompletionStatus,
} from '../../../shared/PreWorkServiceTypes';
import { displayPreworkStatus } from '../../../shared/Utils';
import moment from 'moment';
import { DATE_DO_MMM_YYYY } from '../../../shared/Constants';

const { Text } = Typography;

interface ApplicationSummaryCardProps extends ApplicationPreworkStatus {
	onEvaluateClick: (application: ApplicationSummary) => void;
}

export const ApplicationSummaryCard = (props: ApplicationSummaryCardProps) => {
	const {
		application,
		preworkCompletionStatus,
		preworkMentorCompletionStatus,
		preworkMentorNextAttemptDate,
		onEvaluateClick,
	} = props;
	const {
		applicant: { firstName, lastName, srcmid, gender },
		center,
	} = application;
	const { t } = useTranslation();

	const handleEvaluateClick = useCallback(() => {
		onEvaluateClick(application);
	}, [application, onEvaluateClick]);

	const preworkStatus = t(
		displayPreworkStatus(preworkCompletionStatus, preworkMentorCompletionStatus)
	);

	const disableViewButton =
		(preworkMentorNextAttemptDate &&
			moment().isSameOrBefore(preworkMentorNextAttemptDate)) ||
		preworkCompletionStatus !== PreworkCompletionStatus.COMPLETED ||
		!preworkCompletionStatus;

	const resubmitDate = moment(preworkMentorNextAttemptDate).format(
		DATE_DO_MMM_YYYY
	);

	const showInfoIcon =
		disableViewButton &&
		(preworkMentorCompletionStatus === PreworkCompletionStatus.INCOMPLETE ||
			preworkMentorCompletionStatus ===
				PreworkCompletionStatus.PARTIALLY_COMPLETED);

	return (
		<>
			<div className="flex flex-col rounded-lg border border-solid border-gray-300 bg-card-bg p-[16px] py-[24px]">
				<div className="flex flex-row gap-6 mb-4">
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('fullName')}</Text>
						<Text className="text-gray-650 text-sm font-bold">{`${
							firstName ?? ''
						} ${lastName ?? ''}`}</Text>
					</div>
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">
							{t('preworkStatus')}
						</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{preworkStatus}
						</Text>
					</div>
				</div>

				<div className="flex flex-row gap-6 mb-4">
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('idCardNo')}</Text>
						<Text className="text-gray-650 text-sm font-bold">{srcmid}</Text>
					</div>
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('gender')}</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{gender &&
								gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()}
						</Text>
					</div>
				</div>

				<div className="flex flex-row gap-6 mb-4">
					<div className="flex flex-col gap-1 w-[100%]">
						<Text className="text-light-gray text-sm">{t('zone')}</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{center.zone}
						</Text>
					</div>
				</div>

				<div className="flex flex-col justify-center items-center gap-1">
					<Button
						type="primary"
						onClick={handleEvaluateClick}
						className="w-full"
						disabled={disableViewButton}
					>
						<span className="flex items-center justify-center gap-1">
							{preworkMentorCompletionStatus ===
							PreworkCompletionStatus.COMPLETED
								? t('view')
								: t('review')}{' '}
							<ArrowRightOutlined />
						</span>
					</Button>
					{showInfoIcon && (
						<Text className="text-light-gray text-sm">
							{t('resubmitAfter', { resubmitDate })}
						</Text>
					)}
				</div>
			</div>
		</>
	);
};
